import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import './WorkoutMilestones.css';

const WorkoutMilestones = () => {
    const navigate = useNavigate();
    const [backgroundImage, setBackgroundImage] = useState('');
    const [achievedMilestones, setAchievedMilestones] = useState({});
    const [couponCodes, setCouponCodes] = useState({});

    // All available images for background
    const allImages = [
        'albert-einstein.jpg', 'alexander-the-great.jpg', 'amelia-earhart.jpg', 'spartacus.jpg',
        'king-leonidas.jpg', 'napoleon-bonaparte.jpg', 'julius-caesar.jpg', 'miyamoto-musashi.jpg',
        'hannibal-barca.jpg', 'attila-the-hun.jpg', 'blackbeard.jpg', 'boudica.jpg', 'thor.jpg',
        'captainamerica.jpg', 'ironman.jpg', 'spiderman.jpg', 'wolverine.jpg', 'deadpool.jpg'
    ];

    // Generate random coupon codes
    const generateCouponCode = (baseCode) => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const randomSuffix = Array.from({length: 4}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
        return `${baseCode}${randomSuffix}`;
    };

    // Enhanced milestone definitions with all 8 fitness tests
    const milestones = [
        // General workout milestones
        {
            id: 'first-workout',
            title: 'First Workout Milestone!',
            description: 'The first step is getting started, and you started today! Congratulations!',
            fullText: 'Starting is often the hardest part of any fitness journey. Today, you chose yourself and prioritized your health and well-being. You took that crucial first step and got your very first workout under your belt. This moment marks the beginning of something incredible - your transformation journey. Every fitness legend started exactly where you are now, with that first workout. You should feel proud of making this commitment to yourself!',
            couponText: 'FIRST10 - 10% off your first workout gear purchase!',
            emoji: '🏃‍♂️'
        },
        {
            id: 'same-workout-twice',
            title: 'Consistency Champion!',
            description: 'You completed the same workout twice - building consistency!',
            fullText: 'Consistency is the key to lasting results, and you\'ve just proven you have what it takes! By completing the same workout twice, you\'ve shown dedication and commitment to your routine. You now have a checkmark next to that trainer\'s workout - wear it like a badge of honor! This consistency is what separates those who dream from those who achieve. Now try a new workout and keep this amazing momentum going!',
            couponText: 'CONSISTENT15 - 15% off any training equipment!',
            emoji: '✅'
        },

        // Fitness test milestones
        {
            id: 'first-fitness-test',
            title: 'Battle Academy Initiate!',
            description: 'Completed your first fitness test in Battle Story Mode!',
            fullText: 'Welcome to the Battle Academy! You\'ve taken your first fitness test and proven you have the courage to face challenges head-on. Whether you scored Elite or Beginner, what matters is that you stepped up to the test. Every legendary warrior started with their first trial. Your performance has been recorded and will help guide your training journey. The academy doors are now open to you!',
            couponText: 'ACADEMY20 - 20% off Battle Academy training gear!',
            emoji: '⚔️'
        },

        // Bodyweight test elite achievements
        {
            id: 'elite-pushup-performance',
            title: 'Push-Up Elite Warrior!',
            description: 'Achieved Elite rank on the Push-Up Test!',
            fullText: 'INCREDIBLE! Your push-up performance places you among the elite warriors of the Battle Academy! This exceptional display of upper body strength and endurance has caught the attention of legendary trainers like Spartacus himself. Elite-level performance unlocks exclusive story paths, advanced training programs, and legendary character mentors. You\'ve proven you have the foundation of a true champion!',
            couponText: 'ELITE30 - 30% off elite warrior equipment!',
            emoji: '💪'
        },
        {
            id: 'elite-pullup-performance',
            title: 'Pull-Up Legend!',
            description: 'Achieved Elite rank on the Pull-Up Test!',
            fullText: 'PHENOMENAL! Your pull-up mastery marks you as a functional strength legend! The ability to perform elite-level pull-ups demonstrates exceptional upper body power and grip strength. Ancient warriors who scaled castle walls would be proud of your performance. This achievement unlocks access to advanced combat training and legendary mentors like Miyamoto Musashi. Your functional strength is truly extraordinary!',
            couponText: 'LEGEND35 - 35% off legendary training equipment!',
            emoji: '🏋️'
        },
        {
            id: 'elite-situp-performance',
            title: 'Core Elite Master!',
            description: 'Achieved Elite rank on the Sit-Up Test!',
            fullText: 'OUTSTANDING! Your core strength is absolutely elite! Sit-ups test not just abdominal strength but total core endurance and mental toughness. Your performance demonstrates the kind of core stability that ancient warriors needed for battle. A strong core is the foundation of all movement - you\'ve mastered this fundamental aspect of fitness!',
            couponText: 'CORE30 - 30% off core training equipment!',
            emoji: '🔥'
        },
        {
            id: 'elite-squat-performance',
            title: 'Squat Elite Champion!',
            description: 'Achieved Elite rank on the Bodyweight Squat Test!',
            fullText: 'MAGNIFICENT! Your squat endurance is truly elite! Bodyweight squats test leg strength, hip mobility, and cardiovascular endurance all at once. Your performance shows you have the lower body power and stamina of a true athlete. Ancient warriors needed this kind of leg strength for long marches and battle - you\'ve proven you have warrior-level endurance!',
            couponText: 'LEGS30 - 30% off leg training equipment!',
            emoji: '🦵'
        },
        {
            id: 'elite-balance-performance',
            title: 'Balance Elite Zen Master!',
            description: 'Achieved Elite rank on the Balance Test!',
            fullText: 'EXTRAORDINARY! Your balance control is at a zen master level! Elite balance performance demonstrates exceptional proprioception, core stability, and mind-body connection. This skill transcends fitness - it\'s about mental focus and body awareness. Ancient martial artists spent years developing this level of balance. You\'ve achieved true harmony between mind and body!',
            couponText: 'ZEN35 - 35% off balance and stability equipment!',
            emoji: '⚖️'
        },
        {
            id: 'elite-endurance-performance',
            title: 'Endurance Elite Warrior!',
            description: 'Achieved Elite rank on the Endurance Test!',
            fullText: 'INCREDIBLE! Your cardiovascular endurance is at an elite athlete level! This performance demonstrates exceptional heart and lung capacity, mental toughness, and aerobic fitness. Elite endurance athletes can maintain high intensity for extended periods - a skill that separates champions from the rest. Your cardiovascular system is now a weapon of elite performance!',
            couponText: 'CARDIO35 - 35% off cardio equipment!',
            emoji: '🏃'
        },

        // Strength test elite achievements
        {
            id: 'elite-benchpress-performance',
            title: 'Bench Press Elite Powerhouse!',
            description: 'Achieved Elite rank on the Bench Press Test!',
            fullText: 'PHENOMENAL! Your bench press strength places you among the elite powerlifters! Pressing 1.5x+ your bodyweight demonstrates exceptional upper body power and training dedication. This level of strength is what separates recreational lifters from serious athletes. Ancient warriors would be amazed by your pressing power - you\'ve mastered the art of raw strength!',
            couponText: 'POWER40 - 40% off powerlifting equipment!',
            emoji: '🏋️‍♂️'
        },
        {
            id: 'elite-squatlift-performance',
            title: 'Squat Lift Elite Titan!',
            description: 'Achieved Elite rank on the Squat Lift Test!',
            fullText: 'LEGENDARY! Your squat strength is absolutely titanic! Squatting 2.5x+ your bodyweight puts you in the realm of elite powerlifters and strongmen. The squat is the king of exercises, testing total body strength and mental fortitude. Your legs can now generate the power of ancient war machines. You\'ve achieved true lower body dominance!',
            couponText: 'TITAN45 - 45% off heavy lifting equipment!',
            emoji: '👑'
        },
        {
            id: 'elite-deadlift-performance',
            title: 'Deadlift Elite Beast!',
            description: 'Achieved Elite rank on the Deadlift Test!',
            fullText: 'UNSTOPPABLE! Your deadlift strength is absolutely beastly! Deadlifting 3x+ your bodyweight puts you among the strongest humans on the planet. The deadlift tests total body strength like no other exercise - you\'ve mastered the ultimate display of raw power. Ancient strongmen would bow to your lifting prowess. You are now a force of nature!',
            couponText: 'BEAST50 - 50% off strength training equipment!',
            emoji: '🦍'
        },
        {
            id: 'elite-bentrow-performance',
            title: 'Bent Row Elite Archer!',
            description: 'Achieved Elite rank on the Bent-Over Row Test!',
            fullText: 'EXCEPTIONAL! Your pulling strength rivals legendary archers! Elite bent-over row performance demonstrates exceptional back strength and postural control. This pulling power would allow you to draw the heaviest war bows of ancient times. Your posterior chain strength is now at an elite level - you\'ve mastered the art of pulling power!',
            couponText: 'ARCHER35 - 35% off back training equipment!',
            emoji: '🏹'
        },

        // Multi-achievement milestones
        {
            id: 'fitness-test-improvement',
            title: 'Improvement Champion!',
            description: 'Improved your fitness test score by 25% or more!',
            fullText: 'Outstanding progress! You\'ve demonstrated that dedication and training produce real results. Improving your fitness test performance by 25% or more shows you\'re not just working out - you\'re evolving. This kind of measurable improvement is what separates casual exercisers from true athletes. Your commitment to getting stronger is inspiring and proves that consistent effort pays off!',
            couponText: 'PROGRESS25 - 25% off progress tracking tools!',
            emoji: '📈'
        },
        {
            id: 'multi-test-elite',
            title: 'Multi-Dimensional Elite!',
            description: 'Achieved Elite rank on multiple fitness tests!',
            fullText: 'EXTRAORDINARY! Achieving elite status across multiple fitness tests proves you\'re a complete athlete! This rare accomplishment demonstrates balanced development in strength, endurance, and functional fitness. You\'ve mastered not just one area, but multiple aspects of physical performance. The Battle Academy recognizes you as a true elite warrior with comprehensive abilities. Only the most dedicated athletes reach this level!',
            couponText: 'COMPLETE40 - 40% off complete fitness systems!',
            emoji: '🌟'
        },
        {
            id: 'strength-elite-trio',
            title: 'Strength Elite Trinity!',
            description: 'Achieved Elite rank on Bench Press, Squat, and Deadlift!',
            fullText: 'LEGENDARY! You\'ve mastered the holy trinity of strength training! Elite performance in bench press, squat, and deadlift marks you as a true powerhouse. These three lifts test every major muscle group and movement pattern. Ancient strongmen would recognize you as one of their own. You\'ve achieved the rare distinction of being elite in all three foundational strength movements!',
            couponText: 'TRINITY50 - 50% off powerlifting packages!',
            emoji: '🔱'
        },
        {
            id: 'bodyweight-elite-master',
            title: 'Bodyweight Elite Master!',
            description: 'Achieved Elite rank on all bodyweight tests!',
            fullText: 'INCREDIBLE! You\'ve mastered your own bodyweight completely! Elite performance across push-ups, pull-ups, sit-ups, squats, and balance demonstrates total body control and functional strength. This level of bodyweight mastery is what ancient warriors trained for years to achieve. You need no equipment - your body itself is a perfectly tuned machine!',
            couponText: 'MASTER45 - 45% off bodyweight training equipment!',
            emoji: '🥋'
        },

        // Time-based milestones
        {
            id: 'seven-days',
            title: 'One Week Warrior!',
            description: 'Seven days of workouts logged - you\'re building a habit!',
            fullText: 'One full week of dedication! Scientists say it takes time to build a habit, and you\'re well on your way. Seven consecutive days of choosing fitness over excuses shows incredible willpower and determination. You\'ve proven to yourself that you can make fitness a priority no matter what life throws at you. This is the foundation of a lifestyle change that will benefit you for years to come!',
            couponText: 'WEEK1DONE - 20% off weekly workout supplements!',
            emoji: '🗓️'
        },
        {
            id: 'two-weeks',
            title: 'Fortnight Finisher!',
            description: 'Two weeks (14 workouts) logged!',
            fullText: 'Two full weeks of commitment! Fourteen workouts represent serious dedication to your fitness goals. You\'ve moved beyond the initial excitement phase and proven you have staying power. Your body is already adapting, your mind is getting stronger, and your habits are solidifying. This is where many people give up, but you\'re still here, still pushing, still growing!',
            couponText: 'FORTNIGHT25 - 25% off two-week meal plans!',
            emoji: '🏆'
        },
        {
            id: 'six-weeks',
            title: 'Six Week Superstar!',
            description: 'Six weeks of workouts completed!',
            fullText: 'Six weeks of unwavering dedication! This is where transformation becomes visible and undeniable. Your strength has increased, your endurance has improved, and your confidence is soaring. Forty-two workouts represent a lifestyle change, not just a temporary effort. You\'ve built momentum that will carry you forward for months and years to come. You are living proof that consistency creates champions!',
            couponText: 'SIXWEEK35 - 35% off transformation packages!',
            emoji: '🌟'
        },
        {
            id: 'twelve-weeks',
            title: 'Quarter Champion!',
            description: 'Twelve weeks of workouts logged!',
            fullText: 'Three months of excellence! Eighty-four workouts represent a complete body transformation cycle. Your dedication over twelve weeks has created lasting changes in your strength, physique, and mindset. You\'ve proven that you can commit to long-term goals and see them through. This level of consistency places you in the top percentile of fitness enthusiasts worldwide!',
            couponText: 'QUARTER40 - 40% off quarterly training programs!',
            emoji: '💎'
        },
        {
            id: 'twenty-four-weeks',
            title: 'Half-Year Hero!',
            description: 'Twenty-four weeks of workouts logged!',
            fullText: 'Six months of relentless pursuit of excellence! One hundred sixty-eight workouts represent a complete lifestyle transformation. You\'ve weathered every excuse, overcome every obstacle, and maintained consistency through seasons of change. Your dedication is inspiring and your results speak for themselves. You\'ve joined the ranks of true fitness athletes who understand that excellence is a daily choice!',
            couponText: 'HERO50 - 50% off hero-level elite gear!',
            emoji: '👑'
        },

        // Mode-specific milestones
        {
            id: 'build-mode',
            title: 'Master Builder!',
            description: 'First "Build" mode workout completed!',
            fullText: 'Congratulations on using the BUILD system to design your very own workout from scratch! You\'ve moved beyond following preset routines and started creating your own fitness path. This shows creativity, independence, and a deep understanding of your body\'s needs. You\'re not just working out - you\'re becoming a fitness architect, building the exact routine that works for YOU!',
            couponText: 'BUILDER25 - 25% off custom workout gear!',
            emoji: '🏗️'
        },
        {
            id: 'battle-mode',
            title: 'Battle Tested!',
            description: 'First battle mode workout completed!',
            fullText: 'You challenged yourself with a BATTLE mode workout and emerged victorious! This reward recognizes your courage to step into the arena and test your limits. Battle mode isn\'t for the faint of heart - it requires mental toughness, physical determination, and the warrior spirit within you. You\'ve proven you have what it takes to face any challenge head-on!',
            couponText: 'WARRIOR20 - 20% off battle-ready gear!',
            emoji: '⚔️'
        },

        // Special achievements
        {
            id: 'character-collector',
            title: 'Character Collector!',
            description: 'Unlocked 10 different characters through fitness tests!',
            fullText: 'Your diverse fitness performances have unlocked an impressive roster of legendary characters! Each character represents a different aspect of your growing abilities - from Spartacus recognizing your strength to Einstein appreciating your strategic approach to fitness. This collection shows you\'re developing well-rounded fitness skills and earning respect from history\'s greatest figures!',
            couponText: 'COLLECTOR30 - 30% off character unlock bonuses!',
            emoji: '👑'
        },
        {
            id: 'battle-story-progress',
            title: 'Story Mode Explorer!',
            description: 'Completed 5 chapters in Battle Story Mode!',
            fullText: 'You\'ve become deeply invested in your Battle Story Mode journey! Completing 5 chapters shows you\'re not just exercising - you\'re living an adventure. Each chapter has tested different aspects of your fitness while immersing you in epic tales of warrior training. Your character has grown stronger, and so have you! The story continues to evolve based on your real fitness progress.',
            couponText: 'STORY20 - 20% off story mode expansion packs!',
            emoji: '📚'
        },
        {
            id: 'balanced-muscles',
            title: 'Perfect Balance Master!',
            description: 'Achieved perfect muscle group balance!',
            fullText: 'Outstanding achievement! Your workout distribution shows perfect balance across all muscle groups. This means you\'ve been training smart, not just hard. Balanced training prevents injuries, ensures proportional strength development, and creates that coveted athletic physique. Your progress graphs show the discipline and knowledge of a true fitness expert. This award can be earned multiple times - keep up this excellent approach!',
            couponText: 'BALANCE30 - 30% off balanced training equipment!',
            emoji: '⚖️'
        }
    ];

    // Check milestone achievements based on saved workout data and fitness tests
    useEffect(() => {
        const checkMilestones = () => {
            try {
                // Get data from localStorage
                const savedWorkouts = JSON.parse(localStorage.getItem('workoutHistory') || '[]');
                const fitnessTestResults = JSON.parse(localStorage.getItem('fitnessTestResults') || '[]');
                const milestoneData = JSON.parse(localStorage.getItem('achievedMilestones') || '{}');
                const storedCoupons = JSON.parse(localStorage.getItem('couponCodes') || '{}');

                const newAchievements = {...milestoneData};
                const newCoupons = {...storedCoupons};

                // Original workout milestones
                if (savedWorkouts.length >= 1 && !newAchievements['first-workout']) {
                    newAchievements['first-workout'] = {
                        achieved: true,
                        date: new Date().toISOString()
                    };
                    newCoupons['first-workout'] = generateCouponCode('FIRST10');
                }

                // Same workout twice
                const workoutCounts = {};
                savedWorkouts.forEach(workout => {
                    const key = `${workout.workoutType}-${workout.category}`;
                    workoutCounts[key] = (workoutCounts[key] || 0) + 1;
                });

                if (Object.values(workoutCounts).some(count => count >= 2) && !newAchievements['same-workout-twice']) {
                    newAchievements['same-workout-twice'] = {
                        achieved: true,
                        date: new Date().toISOString()
                    };
                    newCoupons['same-workout-twice'] = generateCouponCode('CONSIST15');
                }

                // FITNESS TEST MILESTONES
                if (fitnessTestResults.length >= 1 && !newAchievements['first-fitness-test']) {
                    newAchievements['first-fitness-test'] = {
                        achieved: true,
                        date: new Date().toISOString()
                    };
                    newCoupons['first-fitness-test'] = generateCouponCode('ACADEMY20');
                }

                // Elite performance milestones for all 8 tests
                const eliteTests = fitnessTestResults.filter(test => test.rank === 'elite');
                const testTypes = ['pushup', 'pullup', 'situp', 'squat', 'balance', 'endurance', 'benchpress', 'squatlift', 'deadlift', 'bentrow'];

                testTypes.forEach(testType => {
                    const eliteTestsOfType = eliteTests.filter(test => test.type === testType);
                    const milestoneId = `elite-${testType}-performance`;

                    if (eliteTestsOfType.length >= 1 && !newAchievements[milestoneId]) {
                        newAchievements[milestoneId] = {
                            achieved: true,
                            date: new Date().toISOString(),
                            score: eliteTestsOfType[0].score
                        };

                        // Generate appropriate coupon codes
                        const couponPrefixes = {
                            'pushup': 'ELITE30',
                            'pullup': 'LEGEND35',
                            'situp': 'CORE30',
                            'squat': 'LEGS30',
                            'balance': 'ZEN35',
                            'endurance': 'CARDIO35',
                            'benchpress': 'POWER40',
                            'squatlift': 'TITAN45',
                            'deadlift': 'BEAST50',
                            'bentrow': 'ARCHER35'
                        };

                        newCoupons[milestoneId] = generateCouponCode(couponPrefixes[testType] || 'ELITE30');
                    }
                });

                // Multi-dimensional elite (elite in 2+ different tests)
                const uniqueEliteTests = [...new Set(eliteTests.map(test => test.type))];
                if (uniqueEliteTests.length >= 2 && !newAchievements['multi-test-elite']) {
                    newAchievements['multi-test-elite'] = {
                        achieved: true,
                        date: new Date().toISOString(),
                        tests: uniqueEliteTests
                    };
                    newCoupons['multi-test-elite'] = generateCouponCode('COMPLETE40');
                }

                // Strength Elite Trinity (bench, squat, deadlift all elite)
                const strengthTests = ['benchpress', 'squatlift', 'deadlift'];
                const eliteStrengthTests = strengthTests.filter(test => uniqueEliteTests.includes(test));
                if (eliteStrengthTests.length >= 3 && !newAchievements['strength-elite-trio']) {
                    newAchievements['strength-elite-trio'] = {
                        achieved: true,
                        date: new Date().toISOString(),
                        tests: eliteStrengthTests
                    };
                    newCoupons['strength-elite-trio'] = generateCouponCode('TRINITY50');
                }

                // Bodyweight Elite Master (all bodyweight tests elite)
                const bodyweightTests = ['pushup', 'pullup', 'situp', 'squat', 'balance'];
                const eliteBodyweightTests = bodyweightTests.filter(test => uniqueEliteTests.includes(test));
                if (eliteBodyweightTests.length >= 5 && !newAchievements['bodyweight-elite-master']) {
                    newAchievements['bodyweight-elite-master'] = {
                        achieved: true,
                        date: new Date().toISOString(),
                        tests: eliteBodyweightTests
                    };
                    newCoupons['bodyweight-elite-master'] = generateCouponCode('MASTER45');
                }

                // Fitness test improvement (check for 25%+ improvement)
                const testTypesWithResults = [...new Set(fitnessTestResults.map(test => test.type))];
                for (const testType of testTypesWithResults) {
                    const typeTests = fitnessTestResults
                        .filter(test => test.type === testType)
                        .sort((a, b) => new Date(a.date) - new Date(b.date));

                    if (typeTests.length >= 2) {
                        const firstScore = typeTests[0].score;
                        const latestScore = typeTests[typeTests.length - 1].score;
                        const improvement = ((latestScore - firstScore) / firstScore) * 100;

                        if (improvement >= 25 && !newAchievements['fitness-test-improvement']) {
                            newAchievements['fitness-test-improvement'] = {
                                achieved: true,
                                date: new Date().toISOString(),
                                improvement: Math.round(improvement),
                                testType: testType
                            };
                            newCoupons['fitness-test-improvement'] = generateCouponCode('PROGRESS25');
                        }
                    }
                }

                // Time-based milestones
                const timeMilestones = [
                    { days: 7, id: 'seven-days', coupon: 'WEEK1' },
                    { days: 14, id: 'two-weeks', coupon: 'FORT25' },
                    { days: 42, id: 'six-weeks', coupon: 'SIX35' },
                    { days: 84, id: 'twelve-weeks', coupon: 'QUARTER40' },
                    { days: 168, id: 'twenty-four-weeks', coupon: 'HERO50' }
                ];

                timeMilestones.forEach(({ days, id, coupon }) => {
                    if (savedWorkouts.length >= days && !newAchievements[id]) {
                        newAchievements[id] = {
                            achieved: true,
                            date: new Date().toISOString()
                        };
                        newCoupons[id] = generateCouponCode(coupon);
                    }
                });

                // Mode-specific milestones
                const buildWorkouts = savedWorkouts.filter(w => w.category === 'build' || w.workoutType?.includes('build'));
                if (buildWorkouts.length >= 1 && !newAchievements['build-mode']) {
                    newAchievements['build-mode'] = {
                        achieved: true,
                        date: new Date().toISOString()
                    };
                    newCoupons['build-mode'] = generateCouponCode('BUILD25');
                }

                const battleWorkouts = savedWorkouts.filter(w => w.category === 'battle' || w.workoutType?.includes('battle'));
                if (battleWorkouts.length >= 1 && !newAchievements['battle-mode']) {
                    newAchievements['battle-mode'] = {
                        achieved: true,
                        date: new Date().toISOString()
                    };
                    newCoupons['battle-mode'] = generateCouponCode('WARRIOR20');
                }

                // Character collector milestone
                const characterUnlocks = JSON.parse(localStorage.getItem('unlockedCharacters') || '[]');
                if (characterUnlocks.length >= 10 && !newAchievements['character-collector']) {
                    newAchievements['character-collector'] = {
                        achieved: true,
                        date: new Date().toISOString(),
                        count: characterUnlocks.length
                    };
                    newCoupons['character-collector'] = generateCouponCode('COLLECTOR30');
                }

                // Save updated achievements and coupons
                localStorage.setItem('achievedMilestones', JSON.stringify(newAchievements));
                localStorage.setItem('couponCodes', JSON.stringify(newCoupons));

                setAchievedMilestones(newAchievements);
                setCouponCodes(newCoupons);

            } catch (error) {
                console.error('Error checking milestones:', error);
            }
        };

        checkMilestones();
    }, []);

    // Select random background image on component mount
    useEffect(() => {
        const randomIndex = Math.floor(Math.random() * allImages.length);
        const selectedImage = allImages[randomIndex];
        setBackgroundImage(`/assets/images/${selectedImage}`);
    }, []);

    // Handle back navigation
    const handleBack = () => {
        try {
            soundManager.playButtonSound();
        } catch (error) {
            console.warn('Sound error:', error);
        }
        navigate(-1);
    };

    // Handle copying coupon code
    const handleCopyCoupon = (couponCode) => {
        navigator.clipboard.writeText(couponCode);
        try {
            soundManager.playButtonSound();
        } catch (error) {
            console.warn('Sound error:', error);
        }
    };

    return (
        <div
            className="milestones-container"
            style={{
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div className="milestones-overlay"></div>

            <header className="milestones-header">
                <button className="back-button" onClick={handleBack}>
                    ← Back
                </button>
                <h1>🏆 Workout Milestones</h1>
                <p>Celebrate your fitness achievements and unlock exclusive rewards!</p>
            </header>

            <div className="milestones-grid">
                {milestones.map((milestone) => {
                    const isAchieved = achievedMilestones[milestone.id]?.achieved;
                    const couponCode = couponCodes[milestone.id];

                    return (
                        <div
                            key={milestone.id}
                            className={`milestone-card ${isAchieved ? 'achieved' : 'locked'}`}
                        >
                            <div className="milestone-icon">
                                {isAchieved ? '🏆' : '🔒'}
                                <span className="milestone-emoji">{milestone.emoji}</span>
                            </div>

                            <div className="milestone-content">
                                <h3 className={isAchieved ? 'achieved-title' : 'locked-title'}>
                                    {milestone.title}
                                </h3>

                                <p className={isAchieved ? 'achieved-description' : 'locked-description'}>
                                    {milestone.description}
                                </p>

                                {isAchieved && (
                                    <div className="milestone-details">
                                        <p className="milestone-full-text">
                                            {milestone.fullText}
                                        </p>

                                        {couponCode && (
                                            <div className="coupon-section">
                                                <h4>🎁 Exclusive Reward!</h4>
                                                <p className="coupon-description">{milestone.couponText}</p>
                                                <div className="coupon-code-container">
                                                    <span className="coupon-code">{couponCode}</span>
                                                    <button
                                                        className="copy-coupon-btn"
                                                        onClick={() => handleCopyCoupon(couponCode)}
                                                    >
                                                        Copy
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {achievedMilestones[milestone.id]?.date && (
                                            <p className="achievement-date">
                                                Achieved: {new Date(achievedMilestones[milestone.id].date).toLocaleDateString()}
                                            </p>
                                        )}

                                        {/* Show additional achievement details */}
                                        {achievedMilestones[milestone.id]?.score && (
                                            <p className="achievement-detail">
                                                Score: {achievedMilestones[milestone.id].score} {milestone.id.includes('weight') || milestone.id.includes('bench') || milestone.id.includes('squat') || milestone.id.includes('deadlift') || milestone.id.includes('row') ? 'lbs' : 'reps'}
                                            </p>
                                        )}

                                        {achievedMilestones[milestone.id]?.improvement && (
                                            <p className="achievement-detail">
                                                Improvement: {achievedMilestones[milestone.id].improvement}% in {achievedMilestones[milestone.id].testType}
                                            </p>
                                        )}

                                        {achievedMilestones[milestone.id]?.tests && (
                                            <p className="achievement-detail">
                                                Elite Tests: {achievedMilestones[milestone.id].tests.join(', ')}
                                            </p>
                                        )}

                                        {achievedMilestones[milestone.id]?.count && (
                                            <p className="achievement-detail">
                                                Characters Unlocked: {achievedMilestones[milestone.id].count}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {!isAchieved && (
                                    <p className="locked-hint">
                                        Keep working out to unlock this milestone!
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <footer className="milestones-footer">
                <p>🎯 Keep pushing your limits to unlock more achievements!</p>
                <p>💪 Each milestone brings you closer to your fitness goals!</p>
                <p>⚔️ All 8 Battle Academy fitness tests now tracked!</p>
                <p>🏋️ Elite achievements for: Push-ups, Pull-ups, Sit-ups, Squats, Balance, Endurance, Bench Press, Squat Lifts, Deadlifts, and Bent Rows!</p>
            </footer>
        </div>
    );
};

export default WorkoutMilestones;